import patternClusteringEndpoints from './endpoints/patternClustering.endpoints';

/**
 * Get behavioral features for a subject.
 * @param {number} alienId - The alien/subject identifier.
 * @param {number} [days=30] - Number of days to analyze.
 * @returns {Promise<Object|null>} Behavioral features with 8 metrics.
 */
export async function getBehavioralFeatures(alienId, days = 30) {
  try {
    const response = await patternClusteringEndpoints.getFeatures(alienId, { params: { days } });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    if (error.response?.status === 204) {
      console.log(`[getBehavioralFeatures] No features available for alien ${alienId}`);
      return null;
    }
    console.error('[getBehavioralFeatures] Error:', error);
    return null;
  }
}

/**
 * Get cluster assignment for a subject.
 * @param {number} alienId - The alien/subject identifier.
 * @returns {Promise<Object|null>} Cluster assignment with label and quality scores.
 */
export async function getClusterAssignment(alienId) {
  try {
    const response = await patternClusteringEndpoints.getAssignment(alienId);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      console.log(`[getClusterAssignment] No cluster assignment found for alien ${alienId}`);
      return null;
    }
    console.error('[getClusterAssignment] Error:', error);
    return null;
  }
}

/**
 * Get profile for a specific cluster.
 * @param {number} clusterId - The cluster identifier.
 * @returns {Promise<Object|null>} Cluster profile with characteristics.
 */
export async function getClusterProfile(clusterId) {
  try {
    const response = await patternClusteringEndpoints.getProfile(clusterId);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      console.log(`[getClusterProfile] Cluster ${clusterId} not found`);
      return null;
    }
    console.error('[getClusterProfile] Error:', error);
    return null;
  }
}

/**
 * Get all cluster profiles.
 * @returns {Promise<Array|null>} List of all cluster profiles.
 */
export async function getAllClusterProfiles() {
  try {
    const response = await patternClusteringEndpoints.getAllProfiles();
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      console.log('[getAllClusterProfiles] No cluster profiles found');
      return null;
    }
    console.error('[getAllClusterProfiles] Error:', error);
    return null;
  }
}

/**
 * Get 2D visualization data for cluster display.
 * @param {number} [limit=100] - Maximum number of points to return.
 * @returns {Promise<Object|null>} Visualization data with 2D coordinates.
 */
export async function getClusterVisualization(limit = 100) {
  try {
    const response = await patternClusteringEndpoints.getVisualization({ params: { limit } });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      console.log('[getClusterVisualization] No visualization data available');
      return null;
    }
    console.error('[getClusterVisualization] Error:', error);
    return null;
  }
}

/**
 * Train or retrain the clustering model (Admin only).
 * @param {number} [nClusters=5] - Number of clusters.
 * @param {number} [days=30] - Days for feature extraction.
 * @param {number[]} [personIds=null] - Optional specific person IDs.
 * @returns {Promise<Object|null>} Training result with model metrics.
 */
export async function trainClusterModel(nClusters = 5, days = 30, personIds = null) {
  try {
    const response = await patternClusteringEndpoints.train({ nClusters, days, personIds });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    const message = error.response?.data?.message || error.response?.data?.error || error.message;
    console.error('[trainClusterModel] Error:', message);
    throw new Error(message || 'Failed to train clustering model');
  }
}

/**
 * Cluster a specific set of subjects (Admin only).
 * @param {number[]} personIds - List of person IDs to cluster.
 * @param {number} [nClusters=5] - Number of clusters.
 * @param {number} [days=30] - Days for feature extraction.
 * @returns {Promise<Object|null>} Clustering result with assignments.
 */
export async function clusterSubjects(personIds, nClusters = 5, days = 30) {
  try {
    const response = await patternClusteringEndpoints.cluster({ personIds, nClusters, days });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('[clusterSubjects] Error:', error);
    return null;
  }
}

// Cluster label configuration for display
export const CLUSTER_LABEL_CONFIG = {
  routine_stable: {
    label: 'Routine Stable',
    color: '#22C55E',
    bgColor: 'bg-green-100',
    textColor: 'text-green-800',
    description: 'Consistent daily patterns with high routine adherence',
  },
  mobile_regular: {
    label: 'Mobile Regular',
    color: '#3B82F6',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-800',
    description: 'High mobility but predictable travel patterns',
  },
  irregular_patterns: {
    label: 'Irregular Patterns',
    color: '#F59E0B',
    bgColor: 'bg-yellow-100',
    textColor: 'text-yellow-800',
    description: 'Variable schedules with less predictable behavior',
  },
  night_active: {
    label: 'Night Active',
    color: '#8B5CF6',
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-800',
    description: 'Higher activity during nighttime hours',
  },
  high_risk_exposure: {
    label: 'High-Risk Exposure',
    color: '#EF4444',
    bgColor: 'bg-red-100',
    textColor: 'text-red-800',
    description: 'Frequent visits to high-risk zones',
  },
};

// Feature configuration for display
export const FEATURE_CONFIG = {
  avgDailyDistanceMiles: {
    label: 'Avg Daily Distance',
    unit: 'miles',
    description: 'Average distance traveled per day',
    format: (v) => v?.toFixed(1) || '0',
  },
  uniqueLocationsPerWeek: {
    label: 'Unique Locations/Week',
    unit: '',
    description: 'Average unique locations visited per week',
    format: (v) => v?.toFixed(1) || '0',
  },
  routineAdherenceScore: {
    label: 'Routine Adherence',
    unit: '%',
    description: 'Score indicating adherence to established routine',
    format: (v) => ((v || 0) * 100).toFixed(0),
  },
  nightActivityRatio: {
    label: 'Night Activity',
    unit: '%',
    description: 'Ratio of activity between 10pm-6am',
    format: (v) => ((v || 0) * 100).toFixed(0),
  },
  weekendActivityRatio: {
    label: 'Weekend Activity',
    unit: '%',
    description: 'Ratio of activity on weekends',
    format: (v) => ((v || 0) * 100).toFixed(0),
  },
  highRiskZoneAffinity: {
    label: 'High-Risk Zone Affinity',
    unit: '%',
    description: 'Risk-weighted high-risk zone visit frequency',
    format: (v) => ((v || 0) * 100).toFixed(0),
  },
  velocityVariance: {
    label: 'Velocity Variance',
    unit: '%',
    description: 'Variance in movement speeds',
    format: (v) => ((v || 0) * 100).toFixed(0),
  },
  dwellTimeEntropy: {
    label: 'Dwell Time Entropy',
    unit: '%',
    description: 'Distribution of time across locations',
    format: (v) => ((v || 0) * 100).toFixed(0),
  },
};

/**
 * Get cluster label display configuration.
 * @param {string} clusterLabel - The cluster label key.
 * @returns {Object} Configuration for the cluster label.
 */
export function getClusterLabelConfig(clusterLabel) {
  return CLUSTER_LABEL_CONFIG[clusterLabel] || {
    label: clusterLabel?.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) || 'Unknown',
    color: '#6B7280',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-800',
    description: 'Unknown cluster type',
  };
}
